import Class from '@/Class';
import ChangeHandler from '@/Utility/ChangeHandler';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import * as CoreFrameInterfaces from '#/CoreFrameInterfaces';
import * as DTO from "coreframetypings/dto";

export default class GenericObject extends Class {

    /**
     * Die PdoId
     */
    public pdoId: string;

    /**
     * Der Typ
     */
    public pdTypeName: string;

    /**
     * Der erzeugende Mandant
     */
    public principalName: string;

    /**
     * Der ObjectIdent
     */
    public objectIdentValue: any;

    public delay: number;

    /**
     * Die Werte
     */
    public values: Record<string, KnockoutObservable<any>>;

    public historizedAssemblyVersion: any;
    public historizationDate: any;
    public isHistorized: boolean;
    public currentHistoryPDO_ID: string;
    public isSingleton: boolean;
    public isPrincipalIndependent: boolean;

    /**
     * Eine Liste der unterdrückten Properties
     */
    private suppressedNotifications: Array<any>;

    /**
     * Ein Observable zur Reevaluierung der modifizierten Properties
     */
    private reevaluateChanges: KnockoutObservable<string | number>;

    /**
     * Eine Liste mit ausstehenden Änderungen
     */
    public changes: KnockoutComputed<any>;

    /**
     * Eine Liste mit ausstehenden Änderungen (verzögerte Benachrichtigung)
     */
    public delayedChanges: KnockoutComputed<any>;


    /**
     * Gibt an, ob Änderungen am GenericObject vorgenommen wurden
     */
    public isModified: KnockoutObservable<boolean>;

    /**
     * Erzwingt ein Update der Properties auch ohne Änderungen der Properties
     */
    public isForcedToUpdate: boolean;


    /**
     * Gibt an, ob ausstehende Änderungen an GenericObject noch nicht an den Server übermittelt wurden
     */
    public hasPendingModifications: KnockoutObservable<boolean>;

    constructor(private subject: CoreFrameInterfaces.IGenericObjectConstructorParameter, delay?: number, isModified?: KnockoutObservable<boolean>) {
        super();

        this.pdoId = this.paramOrDefault(subject.pdoId, '');
        this.pdTypeName = this.paramOrDefault(subject.pdTypeName, null);
        this.principalName = this.paramOrDefault(subject.principalName);
        this.objectIdentValue = this.paramOrDefault(subject.objectIdentValue);
        this.delay = this.paramOrDefault(delay, 600);

        this.values = this.paramOrDefault(subject.values, {}, (values) => {
            for (const key of Object.keys(values)) {
                values[key] = this.observableOrDefault(values[key], null);
            }

            ChangeHandler.applyChangeTracking(values);

            return values;
        });

        // Historisierungsdaten
        this.historizedAssemblyVersion = this.paramOrDefault(subject.historizedAssemblyVersion, null);
        this.historizationDate = this.paramOrDefault(subject.historizationDate, null);
        this.isHistorized = this.paramOrDefault(subject.isHistorized, false);
        let _currentHistoryPDO_ID = subject.currentHistoryPDO_ID == undefined ? subject["currentHistoryPDOID"] : subject.currentHistoryPDO_ID;
        this.currentHistoryPDO_ID = this.paramOrDefault(_currentHistoryPDO_ID, null);

        this.isSingleton = this.paramOrDefault(subject.isSingleton, false);
        this.isPrincipalIndependent = this.paramOrDefault(subject.isPrincipalIndependent, false);

        this.suppressedNotifications = [];
        this.reevaluateChanges = this.observableOrDefault(new Date().toString());

        this.changes = this.defineComputed(() => {
            let force = this.reevaluateChanges();
            let values = ko.toJS(this.values);
            let changes = ChangeHandler.getChangesFromModel(this.values);

            changes = _.pick(changes, (value, prop) => {
                return this.suppressedNotifications.indexOf(prop) === -1;
            });

            return changes;
        });

        this.delayedChanges = this.defineComputed(() => {
            return this.changes();
        }).extend({ waiter: this.delay });

        const isModifiedObs = isModified ? isModified : this.observableOrDefault(subject.isModified, false);
        this.isModified = isModifiedObs.extend({ notify: 'always' });

        this.isForcedToUpdate = false;
        this.hasPendingModifications = this.observableOrDefault(subject.hasPendingModifications, false);


        this.defineSubscription(this.changes, (changes) => {
            changes = _.keys(changes);

            if (changes.length > 0) {
                if (!this.isForcedToUpdate) { //Bei einem Force-Update wird der isModified-Zustand nicht geändert
                    this.isModified(true);
                }
            }
        });

        this.defineSubscription(this.isModified, (isModified) => {
            this.hasPendingModifications(isModified);
        });

        this.isDisposable = this.paramOrDefault(true);
    }

    /**
     * Liefert das Observable zu einem Property im GenericObject
     */
    public getObservable<T = any>(propertyName: string, defaultValue?: T): KnockoutObservable<T> {
        if (!(propertyName in this.values)) {
            this.values[propertyName] = ko.observable();
            ChangeHandler.applyChangeTrackingToObservable(this.values[propertyName]);
        }

        let observable = this.values[propertyName];

        if (defaultValue != null && observable.peek() == null) {
            observable(defaultValue);
        }

        return observable;
    }

    /**
     * Bietet die Möglichkeit, das Lesen und Schreiben eines Observables über ein Computed zu definieren
     */
    public getComputed(propertyName: string, params: any): KnockoutComputed<any> {
        let observable = this.getObservable(propertyName);

        if (params.timeout != null) { //TODO: Toter Code? Wird wohl zum Glück nie verwendet
            if (params.hash != null) {
                let timer = null;
                let oldHash = null;
                let pusher = null;

                let computed = this.defineComputed(() => {
                    let value = observable();
                    let converted = null;

                    ko.ignoreDependencies(() => {
                        if (params.read) {
                            converted = params.read(value);
                        }

                        oldHash = params.hash(converted);
                    });

                    if (pusher != null) {
                        pusher.dispose();
                    }

                    pusher = this.defineComputed(() => {
                        let newHash = params.hash(converted);

                        if (newHash != oldHash) {
                            if (timer != null) {
                                window.clearTimeout(timer);
                            }

                            timer = window.setTimeout(() => {
                                if (params.write) {
                                    params.write(observable, converted);
                                }
                            }, params.timeout);
                        }
                    });

                    return converted;
                });

                return computed;
            } else {
                let value = params.read(observable);

                let waiter = this.defineComputed(() => {
                    ko.toJS(value);
                    return value;
                }).extend({ waiter: params.timeout });

                this.defineSubscription(waiter, (value) => {
                    if (value != null) {
                        params.write(observable, value);
                    }
                });

                return value;
            }
        } else {
            let computed = this.definePureComputed({
                read: () => {
                    if (params.read) {
                        return params.read(observable);
                    }

                    return null;
                },
                write: (value) => {
                    if (params.write) {
                        params.write(observable, value);
                    }
                }
            });

            return computed;
        }
    };

    /**
     * Prüft, ob ein Property in dem GenericObject vorhanden ist
     * @param propertyName Name des Property
     */
    public hasValue(propertyName: string) {
        if (!(propertyName in this.values)) {
            return false;
        }
        return true;
    }

    /**
     * Liefert den aktuellen Wert eines Properties
     */
    public getValue<T = any>(propertyName: string): T {
        if (!this.hasValue(propertyName)) {
            this.values[propertyName] = ko.observable();
            ChangeHandler.applyChangeTrackingToObservable(this.values[propertyName]);
        }

        return this.values[propertyName]();
    }

    public getValueReturnNullIfNotExists(propertyName: string) {
        if (this.hasValue(propertyName)) {
            return this.values[propertyName]();
        }

        return null;
    }

    /**
     * Verhindert Change-Notifications für ein Property
     */
    public suppressNotificationFor(property: string) {
        let index = this.suppressedNotifications.indexOf(property);

        if (index === -1) {
            this.suppressedNotifications.push(property);
            this.reevaluateChanges(new Date().toString());
        }
    }

    /**
     * Erlaubt Change-Notifications für ein Property
     */
    public deliverNotificationFor(property: string) {
        let index = this.suppressedNotifications.indexOf(property);

        if (index > -1) {
            this.suppressedNotifications.splice(index, 1);
            this.reevaluateChanges(new Date().getTime());
        }
    }

    /**
     * Registriert einen Callback für sämtliche Änderungen im GenericObject
     * Die Subscription wird dabei im Callee registriert um ein automatisches Disposing zu gewährleisten
     */
    public onChange(callback: (prop: string, value: any) => void, props: Array<string>, callee: Class, deliverSuppressed?: boolean) {
        for (let prop in this.values) {
            if (props == null || props.indexOf(prop) > -1) {
                callee.defineSubscription(this.values[prop], (value) => {
                    if (deliverSuppressed === true || this.suppressedNotifications.indexOf(prop) === -1) {
                        callback(prop, value);
                    }
                });
            }
        }
    }

    /**
     * Registriert einen Callback für verzögerte Änderungen. 
     * Die Subscription wird dabei im Callee registriert um ein automatisches Disposing zu gewährleisten
     */
    public onDelayedChange(callback: (changes: any) => void, callee) {
        callee.defineSubscription(this.delayedChanges, (changes) => {
            changes = _.pick(changes, (value, prop) => {
                return this.suppressedNotifications.indexOf(prop) === -1;
            });

            let hasChanges = _.keys(changes).length > 0 || this.isForcedToUpdate === true;

            if (hasChanges === true) {
                callback(changes);
            }
        });
    }

    public forceUpdateProperties() {
        this.isForcedToUpdate = true;
        this.reevaluateChanges(new Date().toString());
    }

    public refreshProperties(properties): JQuery.Promise<DTO.IResultDTO> {
        return RADEnvironmentProxy.getInstance().getProperties({
            pdoID: this.pdoId as string,
            properties: properties
        }).then((resultDTO) => {
            if (this.validateStatus(resultDTO)) {
                for (let key in resultDTO.returnValue) {
                    if (key in this.values) {
                        this.values[key](resultDTO.returnValue[key]);
                    } else {
                        this.values[key] = this.observableOrDefault(this.subject.values[key]);
                        ChangeHandler.applyChangeTrackingToObservable(this.values[key]);
                    }
                }

                this.commit();
            }
            return resultDTO;
        });
    }

    /**
     * Aktualisiert das GenericObject mit einem SubjectDTO
     */
    public refresh(subject) {
        if (this.pdoId !== subject.pdoId || this.pdTypeName !== subject.pdTypeName) {
            console.warn('PDO-ID or PDTypeName of GenericObject changed');
        }

        this.pdoId = subject.pdoId;
        this.pdTypeName = subject.pdTypeName;
        this.principalName = subject.principalName;
        this.objectIdentValue = subject.objectIdentValue;

        for (let key in subject.values) {
            let value = subject.values[key];
            if (typeof value == "function")
                value = value();

            if (key in this.values) {
                this.values[key](value);
            } else {
                this.values[key] = this.observableOrDefault(value);
                ChangeHandler.applyChangeTrackingToObservable(this.values[key]);
            }
        }

        this.commit();
    }

    /**
     * Bestätigt die ausstehenden Änderungen und markiert diese als unverändert
     * 1. Kann ohne Parameter aufgerufen werden => Commited alle Werte
     * 2. Kann mit einem String als Parameter aufgerufen werden => Commited nur den Wert mit dem übergebenen Property-Namen
     * 3. Kann mit einem String-Array als Parameter aufgerufen werden => Commited alle Werte der übergebenen Property-Namen
     */
    public commit(propertyNames?: string | Array<string>) {
        if (propertyNames != null && !_.isArray(propertyNames)) {
            propertyNames = _.keys(propertyNames);
        }

        ChangeHandler.commitChangesFromModel(this.values, propertyNames);
    }

    /**
     * Setzt ausstehende Änderungen zurück
     * 1. Kann ohne Parameter aufgerufen werden => Reverted alle Werte
     * 2. Kann mit einem String als Parameter aufgerufen werden => Reverted nur den Wert mit dem übergebenen Property-Namen
     * 3. Kann mit einem String-Array als Parameter aufgerufen werden => Reverted alle Werte der übergebenen Property-Namen
     */
    public revert(propertyNames?: string | Array<string>) {
        if (!_.isArray(propertyNames)) {
            propertyNames = _.keys(propertyNames);
        }

        ChangeHandler.revertChangesFromModel(this.values, propertyNames);
    }

    public whenAllChangesAreCommittedToServer(): JQueryPromise<boolean> {
        const commited = $.Deferred<boolean>();

        window.setTimeout(() => {
            return RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded()
                .then(() => {
                    commited.resolve(true);
                })
        }, this.delay);

        return commited.promise();
}

}